import { useCallback } from "react";

import { CompletedIcon, CompletedSolidIcon, Link12Icon } from "@fluentui/react-icons-mdl2";
import { INavLink, Nav } from "@fluentui/react/lib/Nav";
import { Stack } from "@fluentui/react/lib/Stack";
import { IRenderFunction } from "@fluentui/react/lib/Utilities";

import { IPalette } from "@fluentui/react/lib/Theme";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { INavigationProps } from "./INavigationProps";
import { CheckedState, useNavigationItems } from "./UseNavigationItems";
import { useNavigationStyles } from "./UseNavigationStyles";

export const Navigation = WithTheme((props: INavigationProps): JSX.Element => {
  const {
    project,
    theme: { spacing, palette }
  } = props;
  const styles = useNavigationStyles();
  const { navigationItems, selectedNavigationItemId } = useNavigationItems(project);

  const onRenderLink = useCallback(
    (p: INavLink, defaultRenderer: IRenderFunction<INavLink>) => {
      const state = p.checked as CheckedState;
      return (
        <Stack grow horizontal horizontalAlign="space-between" className={styles.item}>
          <Stack.Item grow className={styles.itemText}>
            {defaultRenderer(p)}
          </Stack.Item>
          <Stack.Item disableShrink className={styles.itemCheck}>
            {getIconByState(state, palette)}
          </Stack.Item>
        </Stack>
      );
    },
    [palette, styles]
  );

  return (
    <Stack disableShrink className={styles.root}>
      <Stack grow className={styles.items} tokens={{ childrenGap: spacing.s2 }}>
        <Nav
          selectedKey={selectedNavigationItemId}
          className={styles.navIcon}
          groups={navigationItems}
          onRenderLink={onRenderLink}
        />
      </Stack>
    </Stack>
  );
});

function getIconByState(state: CheckedState, palette: IPalette): JSX.Element {
  switch (state) {
    case CheckedState.Checked:
      return <CompletedSolidIcon style={{ color: palette.accent }} />;
    case CheckedState.NoResult:
      return <CompletedIcon />;
    case CheckedState.Merged:
      return <Link12Icon style={{ color: palette.accent }} />;
    default:
      return null;
  }
}
